import axios from 'axios';

import {
    successGetTickets,
    failedGetTickets,
    successIsConnected,
    failedIsConnected,
    successCreateTicket,
    failedCreateTicket,
    successAddComment,
    failedAddComment,
    successGetClientNotifyHistories,
    failedGetClientNotifyHistories,
    successGetOpenedTicketCount,
    failedGetOpenedTicketCount,
    successGetNotificationCount,
    failedGetNotificationCount,
    successGetNotifications,
    failedGetNotifications,
    successMarkNotificationAsRead,
    failedMarkNotificationAsRead,
    successMarkAllNotificationsAsRead,
    failedMarkAllNotificationsAsRead,
    successClearAllReadNotifications,
    failedClearAllReadNotifications,
    successRemoveTicketCc,
    failedRemoveTicketCc,
} from '../actions/zendeskActions';
import {
    GET_TICKETS,
    GET_NOTIFICATIONS,
    MARK_NOTIFICATION_AS_READ,
    MARK_ALL_NOTIFICATIONS_AS_READ,
    CLEAR_ALL_READ_NOTIFICATIONS,
} from '../constants/zendeskConstants';
import { API_HOST, HTTP_PROTOCOL } from '../../config';

const getZendeskError = (error) => {
    const response = error && error.response;
    const status = response && response.status;
    const responseData = response && response.data;

    if (status === 400) {
        return {
            type: 'validation',
            message:
                (responseData && (
                    responseData.message || responseData.error
                )) ||
                'Please check the information you entered.',
            fieldErrors:
                (responseData && responseData.field_errors) || {},
        };
    }

    if (status >= 500 || !response) {
        return {
            type: 'unavailable',
            message:
                (responseData && responseData.message) ||
                'We can`t reach the ticketing service right now. Please try again shortly.',
        };
    }

    return {
        type: 'error',
        message:
            (responseData && (
                responseData.message || responseData.error
            )) ||
            'Something went wrong. Please try again.',
    };
};

const getZendeskWriteError = (error) => {
    const response = error && error.response;
    const status = response && response.status;
    const responseData = response && response.data;

    if (status === 400) {
        return {
            type: 'validation',
            message:
                (responseData && (
                    responseData.message || responseData.error
                )) ||
                'Please check the information entered.',
            fieldErrors:
                (responseData && responseData.field_errors) || {},
        };
    }

    if (status >= 500 || !response) {
        return {
            type: 'unavailable',
            message:
                (responseData && responseData.message) ||
                'We can`t reach the ticketing service right now. Your message hasn`t been sent — please try again shortly.',
        };
    }

    return {
        type: 'error',
        message:
            (responseData && (
                responseData.message || responseData.error
            )) ||
            'Something went wrong. Please try again.',
    };
};

export const isConnected = () => {
    const token = localStorage.getItem('token');
    const options = {
        method: 'get',
        headers: { 
            'Content-Type': 'application/json', 
            'Authorization': 'JWT ' + token 
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/zendesk/is_connected/`,
    };
    
    return dispatch =>
        axios(options)
            .then(({ data }) => dispatch(successIsConnected(data)))
            .catch((error) => dispatch(failedIsConnected(error)));
};

/**
 * Get list of Zendesk tickets
 */
export const getTickets = (booking) => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'JWT ' + token,
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/zendesk/tickets/?bookingId=${booking.b_bookingID_Visual}&orderNum=${booking.b_client_order_num}`,
    };

    return dispatch => {
        dispatch({
            type: GET_TICKETS,
        });

        return axios(options)
            .then(({ data }) => {
                dispatch(successGetTickets(data));
                return data;
            })
            .catch((error) => {
                const zendeskError = getZendeskError(error);

                dispatch(
                    failedGetTickets(zendeskError)
                );

                throw zendeskError;
            });
    };
};

/**
 * Upload a single Zendesk ticket attachment
 */
export const uploadAttachment = (
    file,
    onProgress = () => {},
) => {
    const token = localStorage.getItem('token');

    const formData = new FormData();

    formData.append('attachment', file);

    return axios({
        method: 'post',
        headers: {
            'Authorization': 'JWT ' + token,
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/zendesk/tickets/attachments/`,
        data: formData,
        onUploadProgress: (event) => {
            if (!event.total) {
                return;
            }

            const progress = Math.round(
                (event.loaded * 100) / event.total
            );

            onProgress(progress);
        },
    })
        .then(({ data }) => data)
        .catch((error) => {
            throw getZendeskWriteError(error);
        });
};

/**
 * Create Zendesk ticket
 */
export const createTicket = (formData) => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'post',
        headers: {
            'Authorization': 'JWT ' + token,
            'Content-Type': 'multipart/form-data',
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/zendesk/tickets/`,
        data: formData,
    };

    return dispatch =>
        axios(options)
            .then(({ data }) => {
                dispatch(successCreateTicket(data));

                return data;
            })
            .catch((error) => {
                const zendeskError =
                    getZendeskWriteError(error);

                dispatch(
                    failedCreateTicket(zendeskError)
                );

                throw zendeskError;
            });
};

/**
 * Add a comment to a Zendesk ticket.
 */
export const addComment = (ticketId, formData) => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'post',
        headers: {
            'Authorization': 'JWT ' + token,
            'Content-Type': 'multipart/form-data',
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/zendesk/tickets/${ticketId}/comments/`,
        data: formData,
    };

    return dispatch =>
        axios(options)
            .then(({ data }) => {
                dispatch(successAddComment(data));

                return data;
            })
            .catch((error) => {
                const zendeskError = getZendeskWriteError(error);

                dispatch(
                    failedAddComment(zendeskError)
                );

                throw zendeskError;
            });
};

/**
 * Remove an email address from a Zendesk ticket's CC list.
 */
export const removeTicketCc = (ticketId, email) => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'delete',
        headers: {
            'Authorization': 'JWT ' + token,
            'Content-Type': 'application/json',
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/zendesk/tickets/${ticketId}/cc/`,
        data: {
            email,
        },
    };

    return dispatch =>
        axios(options)
            .then(({ data }) => {
                dispatch(successRemoveTicketCc(data));

                return data;
            })
            .catch((error) => {
                const zendeskError = getZendeskWriteError(error);

                dispatch(
                    failedRemoveTicketCc(zendeskError)
                );

                throw zendeskError;
            });
};

export const getClientNotifyHistories = (bookingId) => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'get',
        headers: {
            Authorization: `JWT ${token}`,
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/client-notify-history/?booking=${bookingId}`,
    };

    return (dispatch) =>
        axios(options)
            .then(({ data }) => {
                dispatch(
                    successGetClientNotifyHistories(data),
                );

                return data.results;
            })
            .catch((error) => {
                dispatch(
                    failedGetClientNotifyHistories(error),
                );

                throw error;
            });
};

export const getNotifyHistoryPreview = (id) => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'get',
        headers: {
            Authorization: `JWT ${token}`,
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/client-notify-history/${id}/preview/`,
    };

    return axios(options);
};

export const getOpenedTicketCount = (bookingId) => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'get',
        headers: {
            Authorization: `JWT ${token}`,
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/zendesk/opened_ticket_count/?bookingId=${bookingId}`,
    };

    return dispatch =>
        axios(options)
            .then(({ data }) => {
                dispatch(successGetOpenedTicketCount(data));
                return data;
            })
            .catch((error) => {
                const zendeskError = getZendeskError(error);

                dispatch(
                    failedGetOpenedTicketCount(zendeskError)
                );

                throw zendeskError;
            });
};

export const getNotificationCount = () => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'get',
        headers: {
            Authorization: `JWT ${token}`,
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/notifications/count/`,
    };

    return dispatch =>
        axios(options)
            .then(({ data }) => {
                dispatch(successGetNotificationCount(data));
                return data;
            })
            .catch((error) => {
                dispatch(failedGetNotificationCount(error));
                throw error;
            });
};

export const getNotifications = () => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'get',
        headers: {
            Authorization: `JWT ${token}`,
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/notifications/`,
    };

    return dispatch => {
        dispatch({
            type: GET_NOTIFICATIONS,
        });

        return axios(options)
            .then(({ data }) => {
                dispatch(successGetNotifications(data));
                return data;
            })
            .catch((error) => {
                dispatch(failedGetNotifications(error));
                throw error;
            });
    };
};

export const markNotificationAsRead = (notificationId) => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'patch',
        headers: {
            Authorization: `JWT ${token}`,
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/notifications/${notificationId}/read/`,
    };

    return dispatch => {
        dispatch({
            type: MARK_NOTIFICATION_AS_READ,
            notificationId,
        });

        return axios(options)
            .then(({ data }) => {
                dispatch(successMarkNotificationAsRead(data));
                return data;
            })
            .catch((error) => {
                dispatch(failedMarkNotificationAsRead(error));
                throw error;
            });
    };
};

export const markAllNotificationsAsRead = () => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'patch',
        headers: {
            Authorization: `JWT ${token}`,
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/notifications/read-all/`,
    };

    return dispatch => {
        dispatch({
            type: MARK_ALL_NOTIFICATIONS_AS_READ,
        });

        return axios(options)
            .then(({ data }) => {
                dispatch(successMarkAllNotificationsAsRead(data));
                return data;
            })
            .catch((error) => {
                dispatch(failedMarkAllNotificationsAsRead(error));
                throw error;
            });
    };
};

export const clearAllReadNotifications = () => {
    const token = localStorage.getItem('token');

    const options = {
        method: 'delete',
        headers: {
            Authorization: `JWT ${token}`,
        },
        url: `${HTTP_PROTOCOL}://${API_HOST}/notifications/clear-all/`,
    };

    return dispatch => {
        dispatch({
            type: CLEAR_ALL_READ_NOTIFICATIONS,
        });

        return axios(options)
            .then(({ data }) => {
                dispatch(successClearAllReadNotifications(data));
                return data;
            })
            .catch((error) => {
                dispatch(failedClearAllReadNotifications(error));
                throw error;
            });
    };
};



// WEBPACK FOOTER //
// ./src/state/services/zendeskService.js